import { useState } from 'react';
import { Loader2, Info } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { AnalyticsFilters } from './platform-analytics/AnalyticsFilters';
import { OverviewTab } from './platform-analytics/OverviewTab';
import { AreasTab } from './platform-analytics/AreasTab';
import { OrganisationsTab } from './platform-analytics/OrganisationsTab';
import { PeopleTab } from './platform-analytics/PeopleTab';
import {
  defaultDateRange,
  usePlatformAnalytics,
} from './platform-analytics/usePlatformAnalytics';

export default function PlatformAnalytics() {
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [companyTypeFilter, setCompanyTypeFilter] = useState('');
  const [activePreset, setActivePreset] = useState('30 Days');
  const { loading, error, data, refetch } = usePlatformAnalytics(dateRange, companyTypeFilter);

  const applyPreset = (label: string, days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    setDateRange({
      date_from: from.toISOString().split('T')[0],
      date_to: to.toISOString().split('T')[0],
    });
    setActivePreset(label);
  };

  if (loading && !data.overview) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading engagement summary…</span>
      </div>
    );
  }

  if (error && !data.overview) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive text-lg">{error}</p>
        <button type="button" onClick={refetch} className="mt-4 text-primary underline">Try again</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Portal Engagement</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-3xl">
          See who is using the portal, how often they come back, and which areas they visit.
          Login As sessions are excluded so support activity does not inflate these numbers.
        </p>
      </div>

      <AnalyticsFilters
        activePreset={activePreset}
        dateRange={dateRange}
        companyTypeFilter={companyTypeFilter}
        onPreset={applyPreset}
        onDateChange={(field, value) => {
          setDateRange((prev) => ({ ...prev, [field]: value }));
          setActivePreset('');
        }}
        onCompanyTypeChange={setCompanyTypeFilter}
      />

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Updating…
        </div>
      )}

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="areas">Portal areas</TabsTrigger>
          <TabsTrigger value="organisations">Organisations</TabsTrigger>
          <TabsTrigger value="people">People</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab
            overview={data.overview}
            trend={data.trend}
            companies={data.companies}
          />
        </TabsContent>

        <TabsContent value="areas">
          <AreasTab adoption={data.adoption} features={data.features} />
        </TabsContent>

        <TabsContent value="organisations">
          <OrganisationsTab companies={data.companies} />
        </TabsContent>

        <TabsContent value="people">
          <PeopleTab users={data.users} />
        </TabsContent>
      </Tabs>

      <div className="flex items-start gap-2 rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <p>
          <strong>Strong</strong> engagement means people use several areas regularly.
          <strong> Moderate</strong> and <strong>Low</strong> suggest room to encourage wider adoption.
          When an admin uses Login As, that activity is not counted here.
        </p>
      </div>
    </div>
  );
}
